/**
 * Serviço de Pronúncia (TTS) Unificado - Proxy Google Translate + Web Speech API Nativa
 * TENTATIVA 1: Google Translate Proxy
 * TENTATIVA 2: Fallback Web Speech Nativa
 */

import { isPrerecordedAudioEnabled, speakSingleWordPrerecorded, stopPrerecordedAudio } from './prerecordedTtsService';

let libraryTagsCache: any = null;

export const getBookLanguage = async (bookId: string): Promise<string> => {
  if (!bookId) return 'en';
  try {
    if (!libraryTagsCache) {
      const res = await fetch('https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-description-level-tags.json');
      libraryTagsCache = await res.json();
    }
    const book = libraryTagsCache.find((b: any) => String(b.id) === String(bookId));
    if (book && book.language) {
      return book.language.toLowerCase();
    }
  } catch (e) {
    console.warn("Failed to fetch library tags for language mapping:", e);
  }
  return 'en'; // fallback
};

const languageMap: Record<string, string> = {
  'en': 'en-US',
  'pt': 'pt-BR',
  'fr': 'fr-FR',
  'es': 'es-ES',
  'it': 'it-IT',
  'de': 'de-DE',
  'ru': 'ru-RU',
  'ja': 'ja-JP',
  'zh': 'zh-CN',
  'ar': 'ar-SA',
  'nl': 'nl-NL',
  'Inglês': 'en-US',
  'Francês': 'fr-FR',
  'Italiano': 'it-IT',
  'Espanhol': 'es-ES',
  'Alemão': 'de-DE',
  'Japonês': 'ja-JP',
  'Mandarim': 'zh-CN',
  'Português': 'pt-BR',
  'Português (Brasil)': 'pt-BR'
};

let synth: SpeechSynthesis | null = null;
if (typeof window !== 'undefined') {
  synth = window.speechSynthesis;
}

// Proxy Audio Player
let proxyAudio: HTMLAudioElement | null = null;

// Estado Interno do Leitor de Página
let pageChunks: string[] = [];
let currentChunkIndex = 0;
let currentChunkOffset = 0;
let isPaused = false;
let isReading = false;
let currentRate = 1.0;
let currentLocale = 'en-US';
let currentBookId: string | null = null;
let onPageEndCallback: (() => void) | null = null;
let onPageErrorCallback: ((err: any) => void) | null = null;
let onSyncCallback: ((charIndex: number) => void) | null = null;
let onNextCallback: (() => void) | null = null;
let onPrevCallback: (() => void) | null = null;

// Background Play Support (Controle de travamento no Mobile)
let silentAudio: HTMLAudioElement | null = null;
const getSilentAudio = () => {
  if (typeof window === 'undefined') return null;
  if (!silentAudio) {
    silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFRm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==');
    silentAudio.loop = true;
  }
  return silentAudio;
};

const playSilentAudio = () => {
  const audio = getSilentAudio();
  if (audio) audio.play().catch(() => {});
};

const pauseSilentAudio = () => {
  const audio = getSilentAudio();
  if (audio) audio.pause();
};

const updateMediaSession = (metadata?: { title: string, author: string, cover: string }) => {
  if (typeof window !== 'undefined' && 'mediaSession' in navigator) {
    if (metadata) {
      const isUrlValid = (url: string) => /^(http|https|data|blob):/i.test(url);
      const safeCover = metadata.cover && isUrlValid(metadata.cover) ? metadata.cover : "";

      navigator.mediaSession.metadata = new MediaMetadata({
        title: metadata.title,
        artist: metadata.author,
        artwork: safeCover ? [{ src: safeCover, sizes: '512x512', type: 'image/jpeg' }] : []
      });
    }
    navigator.mediaSession.playbackState = isReading && !isPaused ? 'playing' : (isPaused ? 'paused' : 'none');
    navigator.mediaSession.setActionHandler('play', () => textToSpeechService.resumePageReading());
    navigator.mediaSession.setActionHandler('pause', () => textToSpeechService.pausePageReading());
    navigator.mediaSession.setActionHandler('stop', () => textToSpeechService.stopPageReading());
    navigator.mediaSession.setActionHandler('previoustrack', () => { if (onPrevCallback) onPrevCallback(); });
    navigator.mediaSession.setActionHandler('nexttrack', () => { if (onNextCallback) onNextCallback(); });
  }
};

export const cleanTextForTts = (text: string): string => {
  if (!text) return "";
  return text
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/[^\p{L}\p{N}\s.,!?;:()"'`\-\u4e00-\u9fa5\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af\u3000-\u303f]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const prepareChunks = (text: string, maxLength: number = 200) => {
  const cleaned = cleanTextForTts(text);
  if (!cleaned) return [];
  const chunks: string[] = [];
  const sentences = cleaned.match(/[^.?!;。！？；]+[.?!;。！？；]?/g) || [cleaned];
  let currentChunk = "";
  for (const s of sentences) {
    if ((currentChunk + s).length < maxLength) {
      currentChunk += s;
    } else {
      if (currentChunk) chunks.push(currentChunk.trim());
      currentChunk = s;
    }
  }
  if (currentChunk) chunks.push(currentChunk.trim());
  return chunks;
};

// Fallback logic
const runNativeFallback = async (cleanedText: string, targetLocale: string, rate: number, resolve: () => void, reject: (err: any) => void) => {
  if (!synth) {
    reject("No TTS Engine Available");
    return;
  }
  const utterance = new SpeechSynthesisUtterance(cleanedText);
  utterance.lang = targetLocale;
  utterance.rate = rate;
  
  const voice = await textToSpeechService.getBestVoice(targetLocale);
  if (voice) {
    console.log("[TTS Native Fallback] Voz selecionada:", voice.name);
    utterance.voice = voice;
  }

  utterance.onstart = () => {
    playSilentAudio();
    updateMediaSession();
  };

  utterance.onend = () => {
    pauseSilentAudio();
    updateMediaSession();
    resolve();
  };

  utterance.onerror = (e) => {
    if (e.error === 'canceled' || e.error === 'interrupted') {
      resolve();
      return;
    }
    console.warn("[TTS Native Fallback] Erro na reprodução:", e);
    pauseSilentAudio();
    updateMediaSession();
    reject(e);
  };

  synth.speak(utterance);
};

export const textToSpeechService = {
  isSupported: (): boolean => true, // We support proxy everywhere
  
  unlockAudio: (): void => {
    if (!proxyAudio) {
      proxyAudio = new Audio();
    }
    // iOS Hack: load synchrously
    proxyAudio.load();
    if (synth) {
      try {
        synth.cancel();
        const utterance = new SpeechSynthesisUtterance("");
        utterance.volume = 0;
        synth.speak(utterance);
        synth.cancel();
      } catch (e) {}
    }
  },

  stop: (): void => {
    stopPrerecordedAudio();
    if (proxyAudio) {
      proxyAudio.onended = null;
      proxyAudio.onerror = null;
      proxyAudio.pause();
      proxyAudio.src = "";
    }
    if (synth) synth.cancel();
    isReading = false;
    pauseSilentAudio();
    updateMediaSession();
  },

  getVoices: (): Promise<SpeechSynthesisVoice[]> => {
    return new Promise((resolve) => {
      if (!synth) return resolve([]);
      let voices = synth.getVoices();
      if (voices.length > 0) return resolve(voices);

      const handler = () => {
        synth!.onvoiceschanged = null;
        resolve(synth!.getVoices());
      };
      synth.onvoiceschanged = handler;
      setTimeout(() => resolve(synth!.getVoices()), 1000);
    });
  },

  getBestVoice: async (locale: string) => {
    const voices = await textToSpeechService.getVoices();
    const isAuto = localStorage.getItem('libritui_ttsAuto') !== 'false';
    const savedVoiceURI = localStorage.getItem('libritui_ttsVoiceURI');

    if (!isAuto && savedVoiceURI) {
      const manualVoice = voices.find(v => v.voiceURI === savedVoiceURI);
      if (manualVoice) return manualVoice;
    }
    
    const target = locale.toLowerCase().replace('_', '-');
    const targetShort = target.split('-')[0];

    return voices.find(v => v.lang.toLowerCase().replace('_', '-') === target) || 
           voices.find(v => v.lang.toLowerCase().replace('_', '-').startsWith(targetShort)) || 
           null;
  },

  resolveLocale: (langNameOrCode: string): string => {
    if (!langNameOrCode) return 'en-US';
    if (/^[a-z]{2}-[A-Z]{2}$/.test(langNameOrCode)) return langNameOrCode;
    const normalized = langNameOrCode.toLowerCase();
    return languageMap[normalized] || languageMap[langNameOrCode] || 'en-US';
  },

  speakNativeDeviceTts: (text: string, langNameOrCode: string, rate: number = 0.95): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      if (synth) synth.cancel();
      if (proxyAudio) {
        proxyAudio.pause();
        proxyAudio.src = "";
      }
      const targetLocale = textToSpeechService.resolveLocale(langNameOrCode);
      const cleanedText = cleanTextForTts(text);
      if (!cleanedText) return resolve();
      runNativeFallback(cleanedText, targetLocale, rate, resolve, reject);
    });
  },

  speak: async (text: string, langNameOrCode: string, rate: number = 0.95, bookId?: string): Promise<void> => {
    let dynamicLangCode = langNameOrCode;
    if (bookId) {
      // Fetch official language code mapped from JSON if bookId provided
      dynamicLangCode = await getBookLanguage(bookId);
    }

    // Check if R2 prerecorded audio mode is active and text is a single word
    const trimmedText = text ? text.trim() : '';
    const wordsList = trimmedText.split(/\s+/).filter(Boolean);
    if (isPrerecordedAudioEnabled() && wordsList.length === 1) {
      const prerecordedRes = await speakSingleWordPrerecorded(trimmedText, dynamicLangCode);
      if (prerecordedRes.success) {
        return; // Played successfully via R2!
      }
    }

    try {
      const { PiperTTSHandler } = await import('./PiperTTSHandler');
      const isOfflineReady = await PiperTTSHandler.isOfflineAvailable(dynamicLangCode);
      if (isOfflineReady) {
        const success = await PiperTTSHandler.speakOffline(text, dynamicLangCode, rate);
        if (success) {
          return;
        }
        console.warn("[textToSpeechService] Offline Piper synthesis failed. Falling back to native/online TTS...");
      }
    } catch (err) {
      console.warn("[textToSpeechService] Offline Piper handler failed, falling back:", err);
    }

    return new Promise(async (resolve, reject) => {
      if (synth) synth.cancel();
      if (proxyAudio) {
        proxyAudio.pause();
        proxyAudio.src = "";
      }

      const targetLocale = textToSpeechService.resolveLocale(dynamicLangCode);
      const cleanedText = cleanTextForTts(text);

      if (!cleanedText) return resolve();

      // Fallback directly to native SpeechSynthesis
      console.log("[TTS Native Fallback] Speaking using window.speechSynthesis for:", targetLocale);
      runNativeFallback(cleanedText, targetLocale, rate, resolve, reject);
    });
  },

  updateMetadata: (title: string, author: string, cover: string, onNext?: () => void, onPrev?: () => void) => {
    onNextCallback = onNext || null;
    onPrevCallback = onPrev || null;
    updateMediaSession({ title, author, cover });
  },

  startPageReading: async (text: string, langNameOrCode: string, rate: number = 1.0, onEnd: () => void, onSync: (idx: number) => void, onError?: (err: any) => void, startFromChunkIndex: number = 0, bookId?: string) => {
    if (synth) synth.cancel();
    if (proxyAudio) {
      proxyAudio.pause();
      proxyAudio.src = "";
    }

    let dynamicLangCode = langNameOrCode;
    if (bookId) {
      dynamicLangCode = await getBookLanguage(bookId);
      currentBookId = bookId; // store for chunking
    } else {
      currentBookId = null;
    }

    currentLocale = textToSpeechService.resolveLocale(dynamicLangCode);
    currentRate = rate;
    onPageEndCallback = onEnd;
    onSyncCallback = onSync;
    onPageErrorCallback = onError || null;

    pageChunks = prepareChunks(text);
    currentChunkIndex = startFromChunkIndex;
    currentChunkOffset = 0; // Início da página
    isPaused = false;
    isReading = true;

    if (pageChunks.length > 0 && currentChunkIndex < pageChunks.length) {
      playSilentAudio();
      updateMediaSession();
      textToSpeechService.readNextChunk();
    } else {
      textToSpeechService.stopPageReading();
      if (onPageEndCallback) onPageEndCallback();
    }
  },

  readNextChunk: async () => {
    if (!isReading || isPaused || currentChunkIndex >= pageChunks.length) {
      if (currentChunkIndex >= pageChunks.length && isReading && onPageEndCallback) {
        textToSpeechService.stopPageReading();
        onPageEndCallback();
      }
      return;
    }

    const cleanedText = cleanTextForTts(pageChunks[currentChunkIndex]);
    if (!cleanedText) {
      currentChunkOffset += pageChunks[currentChunkIndex].length + 1;
      currentChunkIndex++;
      textToSpeechService.readNextChunk();
      return;
    }

    if (synth) synth.cancel();
    if (proxyAudio) {
      proxyAudio.pause();
      proxyAudio.src = "";
    }

    const finalRate = currentRate * 0.8;
    
    // As per requirement: Page reading MUST use Native TTS ONLY.
    if (onSyncCallback) {
      onSyncCallback(currentChunkOffset);
    }
    await runNativeChunkFallback(cleanedText, currentLocale, finalRate);
  },

  pausePageReading: () => {
    if (!isReading) return;
    isPaused = true;
    if (proxyAudio) {
      proxyAudio.pause();
    }
    if (synth) synth.cancel();
    pauseSilentAudio();
    updateMediaSession();
  },

  resumePageReading: () => {
    if (!isReading) return;
    isPaused = false;
    playSilentAudio();
    updateMediaSession();
    textToSpeechService.readNextChunk();
  },

  stopPageReading: () => {
    if (proxyAudio) {
      proxyAudio.onended = null;
      proxyAudio.onerror = null;
      proxyAudio.pause();
      proxyAudio.src = "";
    }
    if (synth) synth.cancel();
    isReading = false;
    isPaused = false;
    pageChunks = [];
    currentChunkIndex = 0;
    pauseSilentAudio();
    updateMediaSession();
  },

  updatePageReadingRate: (rate: number) => {
    currentRate = rate;
    if (isReading && !isPaused) {
      textToSpeechService.readNextChunk();
    }
  },

  getStatus: () => ({ isReading, isPaused, currentRate })
};

// Fallback just for chunks
const runNativeChunkFallback = async (cleanedText: string, targetLocale: string, rate: number) => {
  if (!synth) {
    if (isReading && !isPaused && onPageErrorCallback) {
      textToSpeechService.pausePageReading();
      onPageErrorCallback(new Error("No TTS Engine"));
    }
    return;
  }
  const utterance = new SpeechSynthesisUtterance(cleanedText);
  utterance.lang = targetLocale;
  utterance.rate = rate;

  const voice = await textToSpeechService.getBestVoice(targetLocale);
  if (voice) utterance.voice = voice;

  let boundaryFired = false;
  let timerId: any = null;

  utterance.onstart = () => {
    // Fallback: Se o evento onboundary não disparar (comum em vozes online do Google/Chrome),
    // iniciamos um timer simulado baseado na taxa de leitura.
    setTimeout(() => {
      if (!boundaryFired && isReading && !isPaused) {
        const words = cleanedText.split(/\s+/);
        let wordIndices: number[] = [];
        let currIdx = 0;
        for (const w of words) {
          wordIndices.push(currIdx);
          currIdx += w.length + 1;
        }
        
        let wIdx = 0;
        // Velocidade base estimada: ~14 caracteres por segundo ajustado pelo rate do TTS.
        const charsPerMs = (14 * rate) / 1000;
        const startTime = Date.now();
        
        timerId = setInterval(() => {
          if (boundaryFired || !isReading || isPaused) {
            clearInterval(timerId);
            return;
          }
          const elapsed = Date.now() - startTime;
          const expectedChars = elapsed * charsPerMs;
          
          while (wIdx < wordIndices.length - 1 && wordIndices[wIdx + 1] <= expectedChars) {
            wIdx++;
            if (onSyncCallback) {
              onSyncCallback(currentChunkOffset + wordIndices[wIdx]);
            }
          }
        }, 50);
      }
    }, 100);
  };

  utterance.onboundary = (event) => {
    boundaryFired = true;
    if (timerId) clearInterval(timerId);
    
    // Removida a checagem restrita de event.name === 'word' para aceitar qualquer boundary 
    // ou boundary genérico caso o browser use nomes diferentes.
    if (onSyncCallback) {
      onSyncCallback(currentChunkOffset + event.charIndex);
    }
  };

  utterance.onend = () => {
    if (timerId) clearInterval(timerId);
    if (isReading && !isPaused) {
      currentChunkOffset += pageChunks[currentChunkIndex].length + 1;
      currentChunkIndex++;
      textToSpeechService.readNextChunk();
    }
  };
  
  utterance.onerror = (e) => {
    if (timerId) clearInterval(timerId);
    if (e.error === 'canceled' || e.error === 'interrupted') return;
    console.error("[TTS Reader Native Fallback] Error reading chunk:", e);
    if (isReading && !isPaused) {
      textToSpeechService.pausePageReading();
      if (onPageErrorCallback) onPageErrorCallback(e);
    }
  };

  synth.speak(utterance);
};
